import React, { useState, useMemo } from 'react';
import { Tree, Card, Modal, Form, InputNumber, Select, Input, Button, message, Switch, Alert, Tag, Divider } from 'antd';
import {
  ExperimentOutlined,
  FileTextOutlined,
  SendOutlined,
  ToolOutlined,
  LineChartOutlined,
  ClusterOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { DataNode } from 'antd/es/tree';

// ======================== 模板树（只保留需要主动设计收集的模板） ========================

const templateTree: DataNode[] = [
  {
    title: '测量系统分析 (MSA)',
    key: 'msa',
    icon: <ToolOutlined />,
    children: [
      { title: '连续数据 GR&R', key: 'msa_continuous', icon: <FileTextOutlined /> },
      { title: '属性数据 MSA', key: 'msa_attribute', icon: <FileTextOutlined /> },
    ],
  },
  {
    title: '实验设计 (DOE)',
    key: 'doe',
    icon: <ClusterOutlined />,
    children: [
      { title: '全因子实验设计', key: 'doe_full_factorial', icon: <FileTextOutlined /> },
      { title: '部分因子实验设计', key: 'doe_fractional', icon: <FileTextOutlined /> },
      { title: '响应曲面 RSM', key: 'doe_rsm', icon: <FileTextOutlined /> },
      { title: '渐进改进 EVOP', key: 'doe_evop', icon: <FileTextOutlined /> },
      { title: '混料设计', key: 'doe_mixture', icon: <FileTextOutlined /> },
      { title: '田口实验', key: 'doe_taguchi', icon: <FileTextOutlined /> },
    ],
  },
  {
    title: 'SPC 控制图',
    key: 'spc',
    icon: <LineChartOutlined />,
    children: [
      { title: 'Xbar-R 子组采样', key: 'spc_xbar_r', icon: <FileTextOutlined /> },
      { title: 'I-MR 个体采样', key: 'spc_imr', icon: <FileTextOutlined /> },
      { title: '属性图 (P/NP/C/U)', key: 'spc_attribute', icon: <FileTextOutlined /> },
    ],
  },
];

// ======================== 参数定义 ========================

interface FieldDef {
  name: string;
  label: string;
  type: 'number' | 'select' | 'text' | 'switch';
  default?: any;
  options?: { label: string; value: string }[];
  min?: number;
  max?: number;
  advanced?: boolean; // 高级模式才显示
  hint?: string;      // 字段旁的提示
}

interface TemplateConfig {
  title: string;
  fields: FieldDef[];
  hasAdvanced?: boolean;
}

const templateParams: Record<string, TemplateConfig> = {
  // MSA
  msa_continuous: {
    title: '连续数据测量系统分析 (GR&R)',
    fields: [
      { name: 'parts', label: '零件数量', type: 'number', default: 10, min: 5, max: 30, hint: '建议 10 个，覆盖过程变异范围' },
      { name: 'operators', label: '操作员数量', type: 'number', default: 3, min: 2, max: 10 },
      { name: 'trials', label: '重复次数', type: 'number', default: 3, min: 2, max: 5, hint: '建议 2~3 次' },
      { name: 'randomize', label: '随机化测量顺序', type: 'select', default: 'yes', options: [
        { label: '是（推荐）', value: 'yes' },
        { label: '否（教学模式）', value: 'no' },
      ], hint: '教学时可关闭，学员按顺序测量更容易' },
    ],
  },
  msa_attribute: {
    title: '属性数据测量系统分析',
    fields: [
      { name: 'parts', label: '零件数量', type: 'number', default: 50, min: 30, max: 100, hint: '建议 ≥ 50 个' },
      { name: 'operators', label: '操作员数量', type: 'number', default: 3, min: 2, max: 10 },
      { name: 'trials', label: '重复次数', type: 'number', default: 3, min: 2, max: 5 },
      { name: 'randomize', label: '随机化判定顺序', type: 'select', default: 'yes', options: [
        { label: '是（推荐）', value: 'yes' },
        { label: '否（教学模式）', value: 'no' },
      ]},
    ],
  },

  // DOE 全因子
  doe_full_factorial: {
    title: '全因子实验设计',
    hasAdvanced: true,
    fields: [
      { name: 'factors', label: '因子数量', type: 'number', default: 3, min: 2, max: 6, hint: '建议 ≤ 5，否则实验次数会很多' },
      { name: 'levels', label: '水平数', type: 'number', default: 2, min: 2, max: 4, hint: '2水平最常用 (-1, +1)' },
      { name: 'replicates', label: '重复次数', type: 'number', default: 1, min: 1, max: 5, hint: '≥ 2次才能估计纯误差' },
      { name: 'randomize', label: '随机化运行顺序', type: 'select', default: 'yes', options: [
        { label: '是（推荐）', value: 'yes' },
        { label: '否（教学模式）', value: 'no' },
      ], hint: '教学时关闭随机化，学员按标准顺序做实验更容易' },
      { name: 'center_points', label: '中心点数量', type: 'number', default: 0, min: 0, max: 10, advanced: true, hint: '检测曲率效应，仅 2 水平有意义' },
      { name: 'blocks', label: '区组数', type: 'number', default: 1, min: 1, max: 8, advanced: true, hint: '不同天/不同批次做实验时分区组' },
    ],
  },

  // DOE 部分因子
  doe_fractional: {
    title: '部分因子实验设计',
    hasAdvanced: true,
    fields: [
      { name: 'factors', label: '因子数量', type: 'number', default: 5, min: 3, max: 8, hint: '筛选阶段常用 5~7 因子' },
      { name: 'resolution', label: '分辨率', type: 'select', default: 'auto', options: [
        { label: '自动推荐', value: 'auto' },
        { label: 'III — 最少运行，主效应与交互混淆', value: 'III' },
        { label: 'IV — 主效应清晰，交互间混淆', value: 'IV' },
        { label: 'V — 主效应+二阶交互均清晰', value: 'V' },
      ], hint: '不确定时选"自动推荐"' },
      { name: 'replicates', label: '重复次数', type: 'number', default: 1, min: 1, max: 5 },
      { name: 'randomize', label: '随机化运行顺序', type: 'select', default: 'yes', options: [
        { label: '是（推荐）', value: 'yes' },
        { label: '否（教学模式）', value: 'no' },
      ]},
      { name: 'center_points', label: '中心点数量', type: 'number', default: 0, min: 0, max: 10, advanced: true, hint: '检测曲率' },
    ],
  },

  // DOE RSM
  doe_rsm: {
    title: '响应曲面 RSM',
    fields: [
      { name: 'factors', label: '因子数量', type: 'number', default: 2, min: 2, max: 5, hint: '通常 2~3 个关键因子' },
      { name: 'design_type', label: '设计类型', type: 'select', default: 'ccd', options: [
        { label: '中心复合设计 CCD（推荐）', value: 'ccd' },
        { label: 'Box-Behnken', value: 'bbd' },
      ], hint: 'CCD 可旋转，BBD 不需要极端水平' },
      { name: 'center_points', label: '中心点数量', type: 'number', default: 3, min: 1, max: 10, hint: '建议 3~5 个' },
      { name: 'randomize', label: '随机化运行顺序', type: 'select', default: 'yes', options: [
        { label: '是（推荐）', value: 'yes' },
        { label: '否（教学模式）', value: 'no' },
      ]},
    ],
  },

  // DOE EVOP
  doe_evop: {
    title: '渐进改进试验 EVOP',
    fields: [
      { name: 'factors', label: '因子数量', type: 'number', default: 2, min: 2, max: 3, hint: '建议 2~3 因子' },
      { name: 'cycles', label: '循环数', type: 'number', default: 3, min: 2, max: 10, hint: '越多循环信息越充分' },
      { name: 'randomize', label: '随机化运行顺序', type: 'select', default: 'yes', options: [
        { label: '是', value: 'yes' },
        { label: '否', value: 'no' },
      ]},
    ],
  },

  // DOE 混料
  doe_mixture: {
    title: '混料设计',
    fields: [
      { name: 'components', label: '组分数量', type: 'number', default: 3, min: 3, max: 6, hint: '各组分之和 = 100%' },
      { name: 'design_type', label: '设计类型', type: 'select', default: 'simplex_lattice', options: [
        { label: '单纯形格子设计', value: 'simplex_lattice' },
        { label: '单纯形质心设计', value: 'simplex_centroid' },
      ]},
      { name: 'randomize', label: '随机化运行顺序', type: 'select', default: 'yes', options: [
        { label: '是', value: 'yes' },
        { label: '否', value: 'no' },
      ]},
    ],
  },

  // DOE 田口
  doe_taguchi: {
    title: '田口实验',
    fields: [
      { name: 'factors', label: '因子数量', type: 'number', default: 4, min: 2, max: 8 },
      { name: 'levels', label: '水平数', type: 'number', default: 3, min: 2, max: 4, hint: '系统自动选择合适的正交表' },
      { name: 'randomize', label: '随机化运行顺序', type: 'select', default: 'yes', options: [
        { label: '是', value: 'yes' },
        { label: '否（教学模式）', value: 'no' },
      ]},
    ],
  },

  // SPC
  spc_xbar_r: {
    title: 'Xbar-R 控制图数据',
    fields: [
      { name: 'subgroups', label: '子组数', type: 'number', default: 25, min: 15, max: 100, hint: '建议 ≥ 25 组' },
      { name: 'subgroup_size', label: '子组大小', type: 'number', default: 5, min: 2, max: 8, hint: '每组抽样数量，常用 5' },
    ],
  },
  spc_imr: {
    title: 'I-MR 控制图数据',
    fields: [
      { name: 'samples', label: '采样点数', type: 'number', default: 30, min: 20, max: 200, hint: '建议 ≥ 25 个' },
    ],
  },
  spc_attribute: {
    title: '属性控制图数据',
    fields: [
      { name: 'subgroups', label: '检验批次数', type: 'number', default: 25, min: 15, max: 100 },
      { name: 'sample_size', label: '每批样本量', type: 'number', default: 100, min: 10, max: 5000 },
      { name: 'chart_type', label: '控制图类型', type: 'select', default: 'p_chart', options: [
        { label: 'P图（不良率，样本量可变）', value: 'p_chart' },
        { label: 'NP图（不良数，样本量固定）', value: 'np_chart' },
        { label: 'C图（缺陷数，固定检验面积）', value: 'c_chart' },
        { label: 'U图（单位缺陷数，面积可变）', value: 'u_chart' },
      ]},
    ],
  },
};

// ======================== 分辨率推荐 ========================

const resolutionGuide: Record<number, { res: string; runs: number; reason: string }> = {
  3: { res: 'IV', runs: 8, reason: '3因子推荐 Res IV (8次运行)，主效应不与二阶交互混淆' },
  4: { res: 'IV', runs: 16, reason: '4因子推荐 Res IV (16次运行)，主效应清晰' },
  5: { res: 'III', runs: 8, reason: '5因子筛选推荐 Res III (8次运行)，快速识别关键因子' },
  6: { res: 'III', runs: 8, reason: '6因子筛选推荐 Res III (8次运行)，最经济的筛选方案' },
  7: { res: 'III', runs: 8, reason: '7因子推荐 Res III (8次运行)，Plackett-Burman 风格' },
  8: { res: 'III', runs: 16, reason: '8因子推荐 Res III (16次运行)，大因子数筛选' },
};

// ======================== 运行次数计算 ========================

function calcRuns(type: string, values: Record<string, any>): number | null {
  const f = values.factors || 3;
  const l = values.levels || 2;
  const r = values.replicates || 1;
  const cp = values.center_points || 0;

  switch (type) {
    case 'doe_full_factorial':
      return Math.pow(l, f) * r + cp;
    case 'doe_fractional': {
      const res = values.resolution || 'auto';
      let runs: number;
      if (res === 'auto') {
        runs = resolutionGuide[f]?.runs || Math.pow(2, Math.ceil(Math.log2(f + 1)));
      } else if (res === 'V') {
        runs = Math.pow(2, f);
      } else if (res === 'IV') {
        runs = Math.pow(2, Math.max(f - 1, 3));
      } else {
        runs = Math.pow(2, Math.max(Math.ceil(Math.log2(f + 1)), 3));
      }
      return runs * r + cp;
    }
    case 'doe_rsm':
      if (values.design_type === 'bbd') return f * (f - 1) * 2 + cp;
      return Math.pow(2, f) + 2 * f + cp;
    case 'msa_continuous':
      return (values.parts || 10) * (values.operators || 3) * (values.trials || 3);
    case 'msa_attribute':
      return (values.parts || 50) * (values.operators || 3) * (values.trials || 3);
    default:
      return null;
  }
}

// ======================== Component ========================

const DataTemplate: React.FC = () => {
  const navigate = useNavigate();
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [modalVisible, setModalVisible] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [form] = Form.useForm();

  const config = templateParams[selectedKey];

  const handleSelect = (keys: React.Key[]) => {
    const key = keys[0] as string;
    if (key && templateParams[key]) {
      setSelectedKey(key);
      setModalVisible(true);
      setAdvancedMode(false);
      form.resetFields();
      const defaults: Record<string, any> = {};
      templateParams[key].fields.forEach((f) => {
        if (f.default !== undefined) defaults[f.name] = f.default;
      });
      form.setFieldsValue(defaults);
    }
  };

  const handleGenerate = async () => {
    try {
      const values = await form.validateFields();
      navigate(`/templates/preview?type=${selectedKey}&params=${encodeURIComponent(JSON.stringify(values))}`);
      setModalVisible(false);
    } catch {
      // validation error
    }
  };

  // 实时计算运行次数
  const watchedValues = Form.useWatch([], form);
  const runCount = useMemo(() => {
    if (!selectedKey || !watchedValues) return null;
    return calcRuns(selectedKey, watchedValues);
  }, [selectedKey, watchedValues]);

  // 分辨率提示
  const resHint = useMemo(() => {
    if (selectedKey !== 'doe_fractional' || !watchedValues) return null;
    const f = watchedValues.factors;
    const res = watchedValues.resolution;
    if (res === 'auto' && f && resolutionGuide[f]) {
      return resolutionGuide[f];
    }
    return null;
  }, [selectedKey, watchedValues]);

  const handleChat = () => {
    if (!chatInput.trim()) return;
    const input = chatInput.trim().toLowerCase();

    // 模板关键词匹配
    const keywordMap: Array<{ keywords: string[]; key: string; label: string }> = [
      { keywords: ['grr', 'gr&r', '量具', '测量系统', 'msa', '重复性', '再现性'], key: 'msa_continuous', label: '连续数据 GR&R' },
      { keywords: ['属性msa', '属性测量', 'kappa', '合格不合格判定'], key: 'msa_attribute', label: '属性数据 MSA' },
      { keywords: ['全因子', 'full factorial', '所有组合'], key: 'doe_full_factorial', label: '全因子实验设计' },
      { keywords: ['部分因子', 'fractional', '筛选', '分辨率'], key: 'doe_fractional', label: '部分因子实验设计' },
      { keywords: ['响应曲面', 'rsm', 'ccd', 'box-behnken', '优化'], key: 'doe_rsm', label: '响应曲面 RSM' },
      { keywords: ['evop', '渐进改进', '生产中实验'], key: 'doe_evop', label: 'EVOP' },
      { keywords: ['混料', '配方', '组分', 'mixture'], key: 'doe_mixture', label: '混料设计' },
      { keywords: ['田口', 'taguchi', '正交表', '稳健'], key: 'doe_taguchi', label: '田口实验' },
      { keywords: ['xbar', '均值极差', '子组'], key: 'spc_xbar_r', label: 'Xbar-R 控制图' },
      { keywords: ['imr', 'i-mr', '个体值', '移动极差'], key: 'spc_imr', label: 'I-MR 控制图' },
      { keywords: ['p图', 'np图', 'c图', 'u图', '不良率', '缺陷数', '属性控制'], key: 'spc_attribute', label: '属性控制图' },
    ];

    for (const entry of keywordMap) {
      if (entry.keywords.some((kw) => input.includes(kw))) {
        message.success(`已匹配「${entry.label}」模板`);
        setSelectedKey(entry.key);
        setModalVisible(true);
        setAdvancedMode(false);
        form.resetFields();
        const defaults: Record<string, any> = {};
        templateParams[entry.key].fields.forEach((f) => {
          if (f.default !== undefined) defaults[f.name] = f.default;
        });
        form.setFieldsValue(defaults);
        setChatInput('');
        return;
      }
    }
    message.info('未匹配到模板，请尝试关键词如：GR&R、全因子、Xbar-R、混料 等');
    setChatInput('');
  };

  return (
    <div style={{ display: 'flex', gap: 24, minHeight: 'calc(100vh - 180px)' }}>
      <Card
        title={<span style={{ color: '#061b31', fontWeight: 600 }}>数据采集模板</span>}
        style={{ width: 300, flexShrink: 0 }}
        bodyStyle={{ padding: '12px 0' }}
        extra={<Tag color="blue">11 个模板</Tag>}
      >
        <Tree
          showIcon
          treeData={templateTree}
          onSelect={handleSelect}
          defaultExpandAll
          style={{ padding: '0 12px' }}
        />
        <div style={{ padding: '16px 16px 0', fontSize: 12, color: '#999', lineHeight: 1.8 }}>
          <Divider style={{ margin: '8px 0', fontSize: 11 }}>设计原则</Divider>
          只有需要<b>主动设计实验/收集方案</b>的场景才提供模板。
          过程能力、假设检验、回归等分析所需数据在日常生产中自然产生，不需要专门的采集模板。
        </div>
      </Card>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Card
          style={{ flex: 1, marginBottom: 16 }}
          bodyStyle={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
          }}
        >
          <div style={{ textAlign: 'center', color: '#999' }}>
            <ExperimentOutlined style={{ fontSize: 64, marginBottom: 16 }} />
            <p style={{ fontSize: 16 }}>请从左侧选择一个模板类型</p>
            <p style={{ fontSize: 13 }}>选择后配置参数并生成可下载的数据收集表</p>
          </div>
        </Card>

        <div
          style={{
            background: '#fff', borderRadius: 10, padding: '12px 16px',
            display: 'flex', gap: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <Input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder="描述您的需求，如：我需要做一个 3 因子 2 水平的全因子实验"
            onPressEnter={handleChat}
            size="large"
            style={{ flex: 1 }}
          />
          <Button type="primary" icon={<SendOutlined />} size="large" onClick={handleChat}>
            发送
          </Button>
        </div>
      </div>

      {/* 参数配置弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>{config?.title || '参数设置'}</span>
            {config?.hasAdvanced && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 400 }}>
                <SettingOutlined style={{ color: '#533afd' }} />
                <Switch
                  size="small"
                  checked={advancedMode}
                  onChange={setAdvancedMode}
                />
                <span style={{ color: advancedMode ? '#533afd' : '#999' }}>
                  {advancedMode ? '高级模式' : '简单模式'}
                </span>
              </div>
            )}
          </div>
        }
        open={modalVisible}
        onOk={handleGenerate}
        onCancel={() => setModalVisible(false)}
        okText="生成模板"
        cancelText="取消"
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {config?.fields
            .filter((field) => !field.advanced || advancedMode)
            .map((field) => (
              <Form.Item
                key={field.name}
                name={field.name}
                label={
                  <span>
                    {field.label}
                    {field.advanced && <Tag color="purple" style={{ marginLeft: 6, fontSize: 10 }}>高级</Tag>}
                  </span>
                }
                rules={field.type !== 'text' ? [{ required: true, message: `请输入${field.label}` }] : undefined}
                tooltip={field.hint}
              >
                {field.type === 'number' ? (
                  <InputNumber min={field.min} max={field.max} style={{ width: '100%' }} />
                ) : field.type === 'select' ? (
                  <Select options={field.options} />
                ) : (
                  <Input placeholder={field.hint || ''} />
                )}
              </Form.Item>
            ))}
        </Form>

        {/* 分辨率智能推荐 */}
        {resHint && (
          <Alert
            type="info"
            showIcon
            message="分辨率推荐"
            description={resHint.reason}
            style={{ marginBottom: 12 }}
          />
        )}

        {/* 运行次数预估 */}
        {runCount !== null && (
          <div style={{
            background: 'linear-gradient(135deg, #533afd, #6b54fd)',
            color: '#fff', borderRadius: 8, padding: '12px 16px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: 13 }}>预计实验运行次数</span>
            <span style={{ fontSize: 24, fontWeight: 700 }}>{runCount} 次</span>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DataTemplate;
